import { Injector } from '@angular/core';
import { FormActionTypeEnum, SEPAPaymentRailTypeEnum } from '@finxone-platform/form-action';
import {
  APP_ZONES,
  ActionButtonOutcomesType,
  BaseWidgetProperties,
  PaymentStatusScreen,
} from '@finxone-platform/shared/sys-config-types';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { map, take } from 'rxjs';
import { ClearFormDataActionWithId, UpdateFormDataAction } from '../../../actions/form-submission.action';
import {
  CreatePaymentRequest,
  PaymentRequestFailed,
  PaymentRequestSuccess,
} from '../../../actions/payment-request.action';
import { paymentRailOptions } from '../../../services/account-service/account.type';
import { AccountState } from '../../../state/account.state';
import { FormActionState } from '../../../state/form-submision.state';
import { handleRedirection } from '../cta-redirection.utils';

/**
 * Processes a payment request by dispatching actions to create a payment request
 * and update form data. It navigates to different pages based on the payment
 * request's success or failure status. The function utilizes the provided
 * widget properties and injector to interact with the store, actions, and router.
 *
 * @param widgetProperties - The properties of the widget, including navigation details.
 * @param injector - The Angular injector used to retrieve services like Store, Actions, and Router.
 */
export function paymentRequestProcessing(widgetProperties: BaseWidgetProperties, injector: Injector) {
  const store = injector.get(Store);
  const action = injector.get(Actions);
  const formState = store.selectSnapshot(FormActionState.getFormActionState);
  const activeAccountId = store.selectSnapshot(AccountState.getCurrentAccountId);
  if (!widgetProperties['zoneToNavigate']) {
    widgetProperties['zoneToNavigate'] = APP_ZONES.PAYMENT;
  }
  const parsedWidgetProperties = JSON.parse(JSON.stringify(widgetProperties));

  if (formState?.response?.formData) {
    parsedWidgetProperties['urlToNavigate'] = PaymentStatusScreen.PAYMENT_PROCESSING;
    handleRedirection(parsedWidgetProperties, injector, ActionButtonOutcomesType.PROCESSING, true);

    const sepaFormState = store.selectSnapshot(
      FormActionState.getFormActionStateWithId(FormActionTypeEnum.SEPA_PAYMENT),
    )?.formData;

    action
      .pipe(
        ofActionDispatched(PaymentRequestSuccess, PaymentRequestFailed),
        take(1),
        map((res) => {
          const type =
            res instanceof PaymentRequestSuccess
              ? ActionButtonOutcomesType.SUCCESS
              : ActionButtonOutcomesType.FAILURE;
          store.dispatch(new ClearFormDataActionWithId(FormActionTypeEnum.SEPA_PAYMENT));
          store.dispatch(new ClearFormDataActionWithId(FormActionTypeEnum.SEPA_PAYMENT_RAIL_FLAG));
          parsedWidgetProperties['urlToNavigate'] =
            type === ActionButtonOutcomesType.SUCCESS
              ? PaymentStatusScreen.PAYMENT_REQUEST_SENT
              : PaymentStatusScreen.PAYMENT_REQUEST_FAILED;
          handleRedirection(parsedWidgetProperties, injector, type, true);
        }),
      )
      .subscribe();

    store.dispatch(
      new CreatePaymentRequest({
        ...formState?.response?.formData,
        scheme: paymentRailOptions[sepaFormState?.paymentRail as SEPAPaymentRailTypeEnum],
        paymentRailOption: sepaFormState?.paymentRail,
        sourceAccountId: activeAccountId,
      }),
    );
    store.dispatch(
      new UpdateFormDataAction({
        transferDate: `${new Date()}`,
      }),
    );
  }
}
